// src/components/admin/AdminDashboard.jsx
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People,
  Description,
  Warning,
  TrendingUp,
} from '@mui/icons-material';
import api from '../../services/api';

const StatCard = ({ title, value, icon, color = 'primary.main' }) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
        </Box>
        <Box sx={{ color }}>
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalEmployees: 0,
    activeContracts: 0,
    expiringDocuments: 0,
    activePostings: 0,
  });
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setError(null);
      
      // Fetch only employees for now (we know this endpoint works)
      try {
        const employeesResponse = await api.get('/employees');
        setStats(prev => ({
          ...prev,
          totalEmployees: employeesResponse.data.length || 0
        }));
      } catch (err) {
        console.log('Employees endpoint not ready');
      }

      // Try other endpoints but don't fail if they don't exist yet
      try {
        const contractsResponse = await api.get('/contracts');
        setStats(prev => ({
          ...prev,
          activeContracts: contractsResponse.data.filter(c => c.status === 'active').length || 0
        }));
      } catch (err) {
        console.log('Contracts endpoint not ready');
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Some features are still being implemented');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="textSecondary" gutterBottom>
        Welcome to Jegos Agency Management System
      </Typography>

      {error && (
        <Alert severity="info" sx={{ my: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {/* Statistics Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Employees"
            value={stats.totalEmployees}
            icon={<People fontSize="large" />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Contracts"
            value={stats.activeContracts}
            icon={<Description fontSize="large" />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expiring Documents"
            value={stats.expiringDocuments}
            icon={<Warning fontSize="large" />}
            color="warning.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Postings"
            value={stats.activePostings}
            icon={<TrendingUp fontSize="large" />}
            color="info.main"
          />
        </Grid>

        {/* Today's Reminders */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Today's Reminders
            </Typography>
            <Typography color="textSecondary">
              Reminders feature coming soon
            </Typography>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Quick Actions
            </Typography>
            <List>
              <ListItem>
                <ListItemText 
                  primary="Register New Employee"
                  secondary="Add a new worker to the system"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Create Contract"
                  secondary="Generate employment contract"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Generate SODRA Report"
                  secondary="Monthly SAM report generation"
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
